/**
 * Pagination Module
 *
 * Handles per-page selector for pagination components.
 * Creates dynamic functions based on hook_name for backward compatibility.
 * Auto-initializes on elements with data-controller="pagination".
 *
 * Usage in Twig:
 * <select data-controller="pagination" data-pagination-hook="products">
 *     <option value="10">10</option>
 *     <option value="25">25</option>
 * </select>
 *
 * Or legacy inline (will auto-create global function):
 * <select onchange="changePerPageproducts(this.value)">
 */

import { $, $$, on, ready } from '../utils/dom.js';

// Store for pagination managers by hook name
const paginationManagers = {};

/**
 * Change per-page setting and reload page
 * @param {string} perPage - The new per-page value
 */
function changePerPage(perPage) {
    const url = new URL(window.location.href);
    url.searchParams.set('per_page', perPage);
    url.searchParams.set('page', '1'); // Reset to first page
    window.location.href = url.toString();
}

/**
 * Initialize pagination on a select element
 * @param {Element} select - The per-page select element
 */
function initPagination(select) {
    const hookName = select.dataset.paginationHook ||
                     select.id?.replace('per-page-', '') ||
                     'default';

    // Add change handler
    select.addEventListener('change', function() {
        changePerPage(this.value);
    });

    // Store manager
    const manager = {
        changePerPage,
        select
    };

    paginationManagers[hookName] = manager;
    select._pagination = manager;

    // Create legacy global function
    createLegacyFunction(hookName);

    return manager;
}

/**
 * Create legacy global function for backward compatibility
 * @param {string} hookName - The hook name
 */
function createLegacyFunction(hookName) {
    const funcName = `changePerPage${hookName}`;

    if (!window[funcName]) {
        window[funcName] = function(perPage) {
            changePerPage(perPage);
        };
    }
}

/**
 * Get a pagination manager by hook name
 * @param {string} hookName - The hook name
 * @returns {Object|null} The pagination manager
 */
export function getManager(hookName) {
    return paginationManagers[hookName] || null;
}

/**
 * Auto-initialize on page load
 */
ready(() => {
    // Initialize data-controller="pagination" elements
    $$('[data-controller="pagination"]').forEach(initPagination);

    // Find all per-page selects by ID pattern and create legacy functions
    const hookNames = new Set();
    $$('[id^="per-page-"]').forEach(select => {
        const hookName = select.id.replace('per-page-', '');
        if (hookName) {
            hookNames.add(hookName);
            // Only init if not already initialized
            if (!select._pagination) {
                initPagination(select);
            }
        }
    });

    // Always create default
    createLegacyFunction('pagination');
    createLegacyFunction('default');
});

/**
 * Manual initialization
 * @param {Element|string} target - Element or selector
 */
export function init(target) {
    const element = typeof target === 'string' ? $(target) : target;
    if (element) {
        return initPagination(element);
    }
}

export default {
    init,
    getManager,
    changePerPage
};
